<template>
  <b-modal
    v-model="visible"
    :title="editMode ? 'Edit Schedule' : 'New Update Schedule'"
    size="lg"
    @hidden="reset"
  >
    <!-- Edit mode: only name/mode/deadline editable -->
    <template v-if="editMode">
      <b-form-group label="Schedule Name">
        <b-form-input v-model="form.name" placeholder="e.g. Warehouse v1.0.17-rel rollout" />
      </b-form-group>
      <b-form-group label="Execution Mode">
        <b-form-radio-group v-model="form.mode" :options="modeOptions" buttons button-variant="outline-primary" />
        <small class="d-block mt-1 text-muted">
          <b>Auto:</b> fires the moment a device hits the charger in the right state.
          <b>Manual:</b> marks device as ready — you confirm each one.
        </small>
      </b-form-group>
      <b-form-group label="Deadline (days)" description="Days before an un-updated device is marked as expired and shown as overdue.">
        <b-form-input v-model.number="form.deadline_days" type="number" min="1" max="365" />
      </b-form-group>
    </template>

    <!-- Create mode: full wizard -->
    <template v-else>
      <!-- Step indicator -->
      <div class="d-flex mb-4" style="gap:0;">
        <div
          v-for="(step, i) in steps"
          :key="i"
          class="flex-fill text-center py-2 border-bottom"
          :style="{
            borderBottomWidth: '3px !important',
            borderBottomColor: currentStep >= i ? '#5e72e4' : '#dee2e6',
            color: currentStep >= i ? '#5e72e4' : '#adb5bd',
            fontWeight: currentStep === i ? '600' : '400',
            fontSize: '0.85rem',
          }"
        >
          <i :class="step.icon" class="mr-1"></i> {{ step.label }}
        </div>
      </div>

      <!-- Step 0: Basics -->
      <div v-show="currentStep === 0">
        <b-form-group label="Schedule Name *">
          <b-form-input v-model="form.name" placeholder="e.g. Warehouse floor rollout v1.0.17" />
        </b-form-group>

        <b-form-group label="Update Type *">
          <div class="d-flex" style="gap:12px;">
            <div
              v-for="t in typeOptions"
              :key="t.value"
              class="card flex-fill cursor-pointer"
              :class="form.type === t.value ? 'border-primary' : ''"
              style="cursor:pointer;"
              @click="form.type = t.value"
            >
              <div class="card-body text-center py-3">
                <i :class="t.icon" class="fa-2x mb-2" :style="{ color: form.type === t.value ? '#5e72e4' : '#adb5bd' }"></i>
                <div class="font-weight-bold">{{ t.label }}</div>
                <small class="text-muted">{{ t.description }}</small>
              </div>
            </div>
          </div>
        </b-form-group>

        <b-form-group label="Execution Mode *">
          <b-form-radio-group v-model="form.mode" :options="modeOptions" buttons button-variant="outline-primary" />
          <small class="d-block mt-1 text-muted">
            <b>Auto:</b> fires the moment a device hits the charger.
            <b>Manual:</b> marks device ready — you confirm each one from the schedule detail page.
          </small>
        </b-form-group>

        <b-form-group label="Deadline (days)" description="Days before un-updated device is marked overdue.">
          <b-form-input v-model.number="form.deadline_days" type="number" min="1" max="365" />
        </b-form-group>
      </div>

      <!-- Step 1: Target (OTA only) -->
      <div v-show="currentStep === 1 && form.type === 'ota'">
        <b-form-group label="Firmware Version *">
          <div v-if="loadingFirmware" class="text-center py-3">
            <b-spinner small /> Loading firmware...
          </div>
          <div v-else-if="!firmwareList.length" class="text-muted">
            No firmware found for this entity.
          </div>
          <div v-else class="list-group">
            <label
              v-for="fw in firmwareList"
              :key="fw.id"
              class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              :class="form.firmware_id === fw.id ? 'active' : ''"
              style="cursor:pointer;"
            >
              <div>
                <b-form-radio :value="fw.id" v-model="form.firmware_id" class="mr-2" />
                <strong>{{ fw.version }}</strong>
                <b-badge v-if="fw.is_latest" variant="success" class="ml-2">Latest</b-badge>
                <b-badge v-if="fw.has_wifi_firmware" variant="info" class="ml-1">+ WiFi</b-badge>
                <div><small class="text-muted">{{ fw.device_type }} · {{ formatDate(fw.release_date) }}</small></div>
                <div v-if="fw.description"><small class="text-muted">{{ fw.description }}</small></div>
              </div>
            </label>
          </div>
        </b-form-group>
      </div>

      <!-- Step: Scope (for both types, appears at different positions) -->
      <div v-show="currentStep === scopeStep">
        <b-form-group label="Scope *" description="Which devices does this schedule cover?">
          <b-form-radio-group
            v-model="form.scope_type"
            :options="scopeOptions"
            stacked
            @change="onScopeChange"
          />
        </b-form-group>
        <!-- Site picker -->
        <b-form-group v-if="form.scope_type === 'site'" label="Select Site">
          <b-form-select v-model="form.scope_site_id" :options="siteOptions" />
        </b-form-group>

        <!-- Group picker -->
        <b-form-group v-if="form.scope_type === 'group'" label="Select Group">
          <b-form-select v-model="form.scope_group_id" :options="groupOptions" />
        </b-form-group>

        <!-- Device checklist -->
        <b-form-group v-if="form.scope_type === 'device_list'" label="Select Devices">
          <b-form-input
            v-model="deviceSearch"
            placeholder="Search devices..."
            size="sm"
            class="mb-2"
          />
          <div
            class="border rounded"
            style="max-height:260px; overflow-y:auto;"
          >
            <div v-if="loadingDevices" class="text-center py-3">
              <b-spinner small />
            </div>
            <div v-else-if="!filteredDevices.length" class="text-center py-3 text-muted">
              No devices found
            </div>
            <label
              v-else
              v-for="device in filteredDevices"
              :key="device.name"
              class="d-flex align-items-center px-3 py-2 border-bottom"
              style="cursor:pointer; margin:0;"
              :class="form.scope_device_ids.includes(device.name) ? 'bg-light' : ''"
            >
              <b-form-checkbox
                :value="device.name"
                v-model="form.scope_device_ids"
                class="mr-2"
                @change="onDeviceSelectionChanged"
              />
              <div>
                <div class="font-weight-bold">{{ device.name }}</div>
                <small class="text-muted">  {{ device.site || 'No site' }} · {{ device.group || 'No group' }} · {{ device.fw_version }}</small>
              </div>
              <b-badge
                v-if="device.is_charging && device.state?.current === 'UNALLOCATED'"
                variant="success"
                class="ml-auto"
              >
                <i class="fas fa-bolt mr-1"></i>Ready now
              </b-badge>
            </label>
          </div>
          <small class="text-muted mt-1 d-block">{{ form.scope_device_ids.length }} selected</small>
          <div v-if="form.scope_device_ids.length > 0" class="mt-2">
            <div class="font-weight-bold small mb-1">Selected Devices:</div>
            <div class="d-flex flex-wrap" style="gap:4px;">
              <b-badge
                v-for="id in form.scope_device_ids"
                :key="id"
                variant="light"
                class="border text-dark px-2 py-1"
                style="font-size:0.7rem;"
              >
                {{ id }}
                <i
                  class="fas fa-times ml-1 cursor-pointer"
                  @click="form.scope_device_ids = form.scope_device_ids.filter(d => d !== id)"
                ></i>
              </b-badge>
            </div>
          </div>
        </b-form-group>
      </div>

      <!-- Step: Files (provision only) -->
      <div v-show="currentStep === fileStep && form.type === 'provision'">
        <b-form-group label="Select Files to Provision">
          <p class="text-muted small">
            Choose which files will be sent to all devices in the selected scope.
            If none are selected, the system will use the default file set determined per device.
          </p>

          <div v-if="!representativeDeviceId && form.scope_type !== 'entity'" class="alert alert-warning">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            No device found in the selected scope to preview files. Please adjust scope or add devices.
          </div>

          <div v-else>
            <base-button type="primary" @click="openFileSelector">
              <i class="fas fa-folder-open mr-2"></i>
              {{ selectedProvisionCommands.length ? 'Change Selected Files' : 'Select Files' }}
            </base-button>

            <base-button
              v-if="selectedProvisionCommands.length"
              type="outline-secondary"
              size="sm"
              class="ml-2"
              @click="clearSelectedFiles"
            >
              <i class="fas fa-times mr-1"></i> Clear
            </base-button>

            <div v-if="selectedProvisionCommands.length" class="mt-3">
              <b-badge variant="info">{{ selectedProvisionCommands.length }} files selected</b-badge>
              <ul class="list-unstyled mt-2 small">
                <li v-for="cmd in selectedProvisionCommands.slice(0, 5)" :key="cmd.local_file">
                  <i class="fas fa-file mr-1"></i> {{ cmd.local_file }}
                </li>
                <li v-if="selectedProvisionCommands.length > 5" class="text-muted">
                  ... and {{ selectedProvisionCommands.length - 5 }} more
                </li>
              </ul>
            </div>
            <div v-else class="text-info small mt-2">
              <i class="fas fa-info-circle mr-1"></i> No custom files selected. Default files will be used.
            </div>
          </div>
        </b-form-group>
      </div>
    </template>

    <!-- Conflict warning -->
    <b-alert v-if="conflicts.length" show variant="warning" class="mt-3">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      <strong>Conflicts detected.</strong> The following are already enrolled in another active schedule:
      <ul class="mb-0 mt-1">
        <li v-for="c in conflicts" :key="c">{{ c }}</li>
      </ul>
    </b-alert>

    <template #modal-footer>
      <base-button type="secondary" @click="visible = false">Cancel</base-button>

      <!-- Edit mode: single save button -->
      <base-button v-if="editMode" type="primary" :disabled="saving" @click="save">
        <b-spinner v-if="saving" small class="mr-2" />
        Save Changes
      </base-button>

      <!-- Create mode: prev / next / submit -->
      <template v-else>
        <base-button v-if="currentStep > 0" type="outline-secondary" @click="currentStep--">
          <i class="fas fa-chevron-left mr-1"></i> Back
        </base-button>
        <base-button
          v-if="currentStep < maxStep"
          type="primary"
          :disabled="!canAdvance"
          @click="advanceStep"
        >
          Next <i class="fas fa-chevron-right ml-1"></i>
        </base-button>
        <base-button
          v-if="currentStep === maxStep"
          type="primary"
          :disabled="!canSubmit || saving"
          @click="submit"
        >
          <b-spinner v-if="saving" small class="mr-2" />
          Create Schedule
        </base-button>
      </template>
    </template>

    <!-- File selector modal (reused) -->
    <ModalProvisionFileSelect
      ref="fileSelectModal"
      :device-id="representativeDeviceId"
      :entity="activeEntity"
      mode="select"
      :scope-level="effectiveScopeParams.level"
      :scope-site-id="effectiveScopeParams.site_id"
      :scope-group-id="effectiveScopeParams.group_id"
      @files-selected="onFilesSelected"
    />
  </b-modal>
</template>

<script>
import ModalProvisionFileSelect from './ModalProvisionFileSelect.vue'

export default {
  name: 'ModalScheduleCreate',
  components: {
    ModalProvisionFileSelect,
  },

  data() {
    return {
      visible: false,
      editMode: false,
      editId: null,
      currentStep: 0,
      saving: false,
      conflicts: [],
      deviceSearch: '',
      loadingFirmware: false,
      loadingDevices: false,
      firmwareList: [],
      allDevices: [],
      selectedProvisionCommands: [],
      representativeDeviceId: null,

      form: {
        name: '',
        type: 'ota',
        mode: 'manual',
        deadline_days: 7,
        firmware_id: null,
        scope_type: 'entity',
        scope_site_id: null,
        scope_group_id: null,
        scope_device_ids: [],
        provision_commands: [],
      },

      typeOptions: [
        { value: 'ota', label: 'OTA Firmware', icon: 'fas fa-microchip', description: 'Push a new firmware binary' },
        { value: 'provision', label: 'Provision', icon: 'fas fa-file-download', description: 'Push config & data files' },
      ],
      modeOptions: [
        { value: 'manual', text: 'Manual' },
        { value: 'auto', text: 'Auto' },
      ],
      groupSiteMap: {}, 
    }
  },

  computed: {
    activeEntity() {
      return this.$store.getters.activeEntity
    },
    // Dynamic scope options: hide "Specific devices" for provision
    scopeOptions() {
      const baseOptions = [
        { value: 'entity', text: 'All devices in this entity' },
        { value: 'site', text: 'All devices in a site' },  
        { value: 'group', text: 'All devices in a group' },
            { value: 'device_list', text: 'Specific devices' },

      ]
      // Show "Specific devices" only for OTA (firmware)
      if (this.form.type === 'ota') {
        baseOptions.push({ value: 'device_list', text: 'Specific devices' })
      }
      return baseOptions
    },
    effectiveScopeParams() {
      const t = this.form.scope_type;
      if (t === 'group') {
        const site = this.groupSiteMap[this.form.scope_group_id] || null;
        return { level: 'group', site_id: site, group_id: this.form.scope_group_id };
      }
      if (t === 'site') {
        return { level: 'site', site_id: this.form.scope_site_id, group_id: null };
      }
      return { level: null, site_id: null, group_id: null };
    },
    steps() {
      if (this.form.type === 'ota') {
        return [
          { label: 'Basics', icon: 'fas fa-info-circle' },
          { label: 'Firmware', icon: 'fas fa-microchip' },
          { label: 'Scope', icon: 'fas fa-crosshairs' },
        ]
      } else {
        return [
          { label: 'Basics', icon: 'fas fa-info-circle' },
          { label: 'Scope', icon: 'fas fa-crosshairs' },
          { label: 'Files', icon: 'fas fa-file-alt' },
        ]
      }
    },
    maxStep() {
      return this.steps.length - 1
    },
    scopeStep() {
      return this.form.type === 'ota' ? 2 : 1
    },
    fileStep() {
      return this.form.type === 'provision' ? 2 : -1
    },
    groupOptions() {
      const groups = [...new Set(this.allDevices.map(d => d.group).filter(Boolean))]
      return [{ value: null, text: '— Select group —' }, ...groups.map(g => ({ value: g, text: g }))]
    },
    filteredDevices() {
      if (!this.deviceSearch) return this.allDevices
      const q = this.deviceSearch.toLowerCase()
      return this.allDevices.filter(d =>
        d.name.toLowerCase().includes(q) ||
        (d.group || '').toLowerCase().includes(q)
      )
    },
    canAdvance() {
      if (this.currentStep === 0) return !!this.form.name && !!this.form.type
      if (this.currentStep === 1) {
        if (this.form.type === 'ota') return !!this.form.firmware_id
        else {
          if (this.form.scope_type === 'site' && !this.form.scope_site_id) return false
          if (this.form.scope_type === 'group' && !this.form.scope_group_id) return false
          if (this.form.scope_type === 'device_list' && !this.form.scope_device_ids.length) return false
          return true
        }
      }
      return true
    },
    canSubmit() {
      if (!this.form.name) return false
      if (this.form.type === 'ota' && !this.form.firmware_id) return false
      if (this.form.scope_type === 'site' && !this.form.scope_site_id) return false
      if (this.form.scope_type === 'group' && !this.form.scope_group_id) return false
      if (this.form.scope_type === 'device_list' && !this.form.scope_device_ids.length) return false
      return true
    },
    siteOptions() {
      const sites = [...new Set(
        this.allDevices
          .map(d => this.groupSiteMap[d.group] || null)
          .filter(Boolean)
      )]
      return [{ value: null, text: '— Select site —' }, ...sites.map(s => ({ value: s, text: s }))]
    },
  },

  watch: {
    currentStep(step) {
      if (step === 1 && this.form.type === 'ota' && !this.firmwareList.length) {
        this.loadFirmware()
      }
      if (step === this.scopeStep && !this.allDevices.length) {
        this.loadDevices()
      }
      if (step === this.fileStep && this.form.type === 'provision') {
        this.updateRepresentativeDevice()
      }
    },
    'form.scope_type': function() {
      this.representativeDeviceId = null
      this.selectedProvisionCommands = []
    },
    'form.scope_site_id': function() {
      if (this.currentStep === this.fileStep) {
        this.updateRepresentativeDevice()
      }
    },
    'form.scope_group_id': function() {
      if (this.currentStep === this.fileStep) {
        this.updateRepresentativeDevice()
      }
    },
    'form.scope_device_ids': function() {
      if (this.form.scope_type === 'device_list') {
        this.representativeDeviceId = this.form.scope_device_ids[0] || null
      }
    },
  },

  methods: {
    open(existingSchedule = null) {
      this.reset()
      if (existingSchedule) {
        this.editMode = true
        this.editId = existingSchedule.id
        this.form.name = existingSchedule.name
        this.form.mode = existingSchedule.mode
        this.form.deadline_days = existingSchedule.deadline_days
      }
      this.visible = true
    },

    reset() {
      this.editMode = false
      this.editId = null
      this.currentStep = 0
      this.saving = false
      this.conflicts = []
      this.deviceSearch = ''
      this.selectedProvisionCommands = []
      this.representativeDeviceId = null
      this.form = {
        name: '', type: 'ota', mode: 'manual', deadline_days: 7,
        firmware_id: null, scope_type: 'entity',
        scope_site_id: null,  
        scope_group_id: null, scope_device_ids: [],
        provision_commands: [],
      }
    },

    onScopeChange() {
      this.form.scope_group_id = null
      this.form.scope_site_id = null     
      this.form.scope_device_ids = []
      this.representativeDeviceId = null
      this.selectedProvisionCommands = []
      if (this.form.scope_type === 'device_list' && !this.allDevices.length) {
        this.loadDevices()
      }
    },

    onDeviceSelectionChanged() {
      if (this.form.scope_type === 'device_list') {
        this.representativeDeviceId = this.form.scope_device_ids[0] || null
      }
    },

    async loadFirmware() {
      this.loadingFirmware = true
      try {
        const resp = await this.$http.get(`/tenant/${this.activeEntity}/firmware/list/CC2`)
        this.firmwareList = resp.data || []
      } catch (e) {
        console.error('Failed to load firmware', e)
      } finally {
        this.loadingFirmware = false
      }
    },

    async loadDevices() {
      this.loadingDevices = true
      try {
        const resp = await this.$http.get(`/tenant/${this.activeEntity}/device?limit=0&offset=0`)
        const devices = resp.data?.data?.data || []
        this.allDevices = devices

        const groupsResp = await this.$http.get(`/tenant/${this.activeEntity}/dispenser?limit=0&offset=0`)
        const groups = groupsResp.data?.data?.data || []

        const groupSiteMap = {}
        groups.forEach(g => {
          if (g.name && g.location?.site) {
            groupSiteMap[g.name] = g.location.site
          }
        })
        this.groupSiteMap = groupSiteMap
      } catch (e) {
        console.error('Failed to load devices', e)
      } finally {
        this.loadingDevices = false
      }
    },

    async updateRepresentativeDevice() {
      if (this.form.scope_type === 'entity') {
        if (!this.allDevices.length) await this.loadDevices()
        this.representativeDeviceId = this.allDevices[0]?.name || null
      }
      else if (this.form.scope_type === 'site' && this.form.scope_site_id) {
        try {
          const resp = await this.$http.get(`/tenant/${this.activeEntity}/device`, {
            params: { site: this.form.scope_site_id, limit: 1 }
          })
          const devices = resp.data?.data?.data || []
          this.representativeDeviceId = devices[0]?.name || null
        } catch (e) {
          console.error('Failed to fetch representative device by site', e)
          this.representativeDeviceId = null
        }
      }
      else if (this.form.scope_type === 'group' && this.form.scope_group_id) {
        try {
          const resp = await this.$http.get(`/tenant/${this.activeEntity}/device`, {
            params: { group: this.form.scope_group_id, limit: 1 }
          })
          const devices = resp.data?.data?.data || []
          this.representativeDeviceId = devices[0]?.name || null
        } catch (e) {
          console.error('Failed to fetch representative device', e)
          this.representativeDeviceId = null
        }
      } else if (this.form.scope_type === 'device_list') {
        this.representativeDeviceId = this.form.scope_device_ids[0] || null
      }
    },

    openFileSelector() {
      if (this.representativeDeviceId) {
        this.$refs.fileSelectModal.open();
      } else {
        this.updateRepresentativeDevice().then(() => {
          if (this.representativeDeviceId) {
            this.$refs.fileSelectModal.open();
          }
        })
      }
    },

    onFilesSelected(commands) {
      this.selectedProvisionCommands = commands
      this.form.provision_commands = commands
    },

    clearSelectedFiles() {
      this.selectedProvisionCommands = []
      this.form.provision_commands = []
    },

    advanceStep() {
      if (this.currentStep < this.maxStep) {
        this.currentStep++
      }
    },

    async submit() {
      this.saving = true
      this.conflicts = []
      try {
        const payload = {
          name: this.form.name,
          type: this.form.type,
          mode: this.form.mode,
          deadline_days: this.form.deadline_days,
          scope_type: this.form.scope_type,
          scope_site_id: this.form.scope_site_id,
          scope_group_id: this.form.scope_group_id,
          scope_device_ids: this.form.scope_device_ids,
          firmware_id: this.form.firmware_id,
        }
        if (this.form.type === 'provision' && this.selectedProvisionCommands.length) {
          payload.provision_commands = this.selectedProvisionCommands
        }
        await this.$store.dispatch('schedule/create', payload)
        this.visible = false
        this.$emit('created')
      } catch (err) {
        if (err?.status === 409 || err?.conflicts) {
          this.conflicts = err.conflicts || ['Conflict with existing schedule']
        }
      } finally {
        this.saving = false
      }
    },

    async save() {
      this.saving = true
      try {
        await this.$store.dispatch('schedule/update', {
          scheduleId: this.editId,
          data: {
            name: this.form.name,
            mode: this.form.mode,
            deadline_days: this.form.deadline_days,
          },
        })
        this.visible = false
        this.$emit('updated')
      } finally {
        this.saving = false
      }
    },

    formatDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString()
    },
  },
}
</script>